import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import DashboardLayout from "../../../components/layout/DashboardLayout";
import AceAvatar from "../../../components/shared/AceAvatar";
import {
  CONTENT_CATEGORIES,
  CONTENT_STATUS,
  createTip,
  fetchTips,
} from "../../../services/contentService";

function StatusPill({ status }) {
  const map = {
    [CONTENT_STATUS.PUBLISHED]: "is-published",
    [CONTENT_STATUS.REVIEW]: "is-review",
    [CONTENT_STATUS.DRAFT]: "is-draft",
  };

  return (
    <span className={`ac-content-statuspill ${map[status] || ""}`}>
      {status}
    </span>
  );
}

function TipPreview({ form }) {
  return (
    <div className="ac-soft-panel ac-content-previewcard">
      <div className="ac-content-block-title">Vorschau</div>

      <div className="mt-4">
        <div className="ac-content-preview-title">
          {form.title || "Titelvorschau"}
        </div>
        <div className="ac-content-preview-meta">
          {form.category} · {form.status}
        </div>
        <div className="ac-content-preview-text">
          {form.excerpt || "Hier erscheint die Kurzbeschreibung des Tipps."}
        </div>
      </div>
    </div>
  );
}

function CreateTipForm({ onCancel, onSave }) {
  const [form, setForm] = useState({
    title: "",
    category: "Stress",
    excerpt: "",
    body: "",
    status: CONTENT_STATUS.DRAFT,
  });

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    const title = form.title.trim();
    const excerpt = form.excerpt.trim();
    const body = form.body.trim();

    if (!title || !excerpt || !body) {
      alert("Bitte Titel, Kurzbeschreibung und Inhalt ausfüllen.");
      return;
    }

    onSave({
      title,
      category: form.category,
      excerpt,
      body,
      status: form.status,
      author: "Admin",
    });
  };

  return (
    <div className="ac-soft-panel ac-content-formwrap">
      <div className="ac-content-block-head">
        <div>
          <div className="ac-content-block-title">Neuen Tipp erstellen</div>
          <div className="ac-content-block-subtitle">
            Erstelle einen neuen Tipp als Entwurf, zur Prüfung oder direkt veröffentlicht.
          </div>
        </div>

        <AceAvatar variant="motiviert1" size={40} />
      </div>

      <div className="ac-content-formgrid">
        <div className="ac-content-form-main">
          <div className="ac-content-field">
            <label className="ac-content-label">Titel</label>
            <input
              value={form.title}
              onChange={(e) => handleChange("title", e.target.value)}
              placeholder="z. B. 60 Sekunden durchatmen"
              className="ac-content-input"
            />
          </div>

          <div className="ac-content-form-2col">
            <div className="ac-content-field">
              <label className="ac-content-label">Kategorie</label>
              <select
                value={form.category}
                onChange={(e) => handleChange("category", e.target.value)}
                className="ac-select w-full"
              >
                {CONTENT_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div className="ac-content-field">
              <label className="ac-content-label">Status</label>
              <select
                value={form.status}
                onChange={(e) => handleChange("status", e.target.value)}
                className="ac-select w-full"
              >
                <option value={CONTENT_STATUS.DRAFT}>{CONTENT_STATUS.DRAFT}</option>
                <option value={CONTENT_STATUS.REVIEW}>{CONTENT_STATUS.REVIEW}</option>
                <option value={CONTENT_STATUS.PUBLISHED}>{CONTENT_STATUS.PUBLISHED}</option>
              </select>
            </div>
          </div>

          <div className="ac-content-field">
            <label className="ac-content-label">Kurzbeschreibung</label>
            <textarea
              value={form.excerpt}
              onChange={(e) => handleChange("excerpt", e.target.value)}
              rows={3}
              placeholder="Kurze Beschreibung für Liste und Vorschau..."
              className="ac-content-textarea"
            />
          </div>

          <div className="ac-content-field">
            <label className="ac-content-label">Inhalt</label>
            <textarea
              value={form.body}
              onChange={(e) => handleChange("body", e.target.value)}
              rows={8}
              placeholder="Hier steht der eigentliche Tipp..."
              className="ac-content-textarea"
            />
          </div>
        </div>

        <div className="ac-content-form-side">
          <TipPreview form={form} />
        </div>
      </div>

      <div className="ac-content-actions-row">
        <button onClick={handleSave} className="ac-primarybtn ac-content-smallbtn">
          Tipp speichern
        </button>
        <button onClick={onCancel} className="ac-ghostbtn ac-content-smallbtn">
          Abbrechen
        </button>
      </div>
    </div>
  );
}

export default function TipsListPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [tipRows, setTipRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState("");
  const [cat, setCat] = useState("Alle Kategorien");
  const [status, setStatus] = useState("Alle Status");

  const categories = useMemo(() => ["Alle Kategorien", ...CONTENT_CATEGORIES], []);
  const statuses = ["Alle Status", CONTENT_STATUS.DRAFT, CONTENT_STATUS.REVIEW, CONTENT_STATUS.PUBLISHED];

  const isNew = params.get("new") === "1";

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        const data = await fetchTips();
        if (!mounted) return;
        setTipRows(data);
      } catch (error) {
        console.error("Tipps konnten nicht geladen werden:", error);
        if (!mounted) return;
        setTipRows([]);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, []);

  const filtered = useMemo(() => {
    return tipRows
      .filter((t) =>
        q ? `${t.title} ${t.excerpt} ${t.author}`.toLowerCase().includes(q.toLowerCase()) : true
      )
      .filter((t) => (cat === "Alle Kategorien" ? true : t.category === cat))
      .filter((t) => (status === "Alle Status" ? true : t.status === status));
  }, [tipRows, q, cat, status]);

  const handleSaveNewTip = async (newTip) => {
    try {
      await createTip(newTip);
      const refreshed = await fetchTips();
      setTipRows(refreshed);
      setParams({});
    } catch (error) {
      console.error("Tipp konnte nicht gespeichert werden:", error);
      alert("Tipp konnte nicht gespeichert werden.");
    }
  };

  return (
    <DashboardLayout title="Inhalte · Tipps">
      <div className="space-y-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="text-xl font-extrabold text-ac-text">Tipps</div>

          <button onClick={() => navigate("/content")} className="ac-ghostbtn">
            ← Übersicht
          </button>
        </div>

        <section className="ac-soft-panel ac-content-hero">
          <div className="ac-content-hero-grid">
            <div>
              <h2 className="ac-content-hero-title">Tipps Liste</h2>
              <p className="ac-content-hero-text">
                Suchen, filtern, öffnen und neue Tipps vorbereiten.
              </p>
            </div>

            <div className="ac-content-hero-side">
              <div className="ac-content-acebox">
                <AceAvatar variant="motiviert1" size={56} />
                {!isNew ? (
                  <button
                    onClick={() => setParams({ new: "1" })}
                    className="ac-primarybtn ac-content-smallbtn"
                  >
                    + Neuer Tipp
                  </button>
                ) : (
                  <div className="ac-content-acebubble">
                    Ace sagt: Nutze klare Titel und kurze Beschreibungen.
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {isNew && (
          <CreateTipForm
            onCancel={() => setParams({})}
            onSave={handleSaveNewTip}
          />
        )}

        <section className="ac-soft-panel ac-content-block">
          <div className="ac-content-block-head">
            <div>
              <div className="ac-content-block-title">Suchen & Filtern</div>
              <div className="ac-content-block-subtitle">
                Treffer: {filtered.length}
              </div>
            </div>
          </div>

          <div className="ac-content-filtergrid">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Suche: Titel / Inhalt / Autor…"
              className="ac-content-input"
            />

            <select
              value={cat}
              onChange={(e) => setCat(e.target.value)}
              className="ac-select w-full"
            >
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>

            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="ac-select w-full"
            >
              {statuses.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </section>

        <section className="ac-soft-panel ac-content-block">
          <div className="ac-content-tablewrap">
            <table className="ac-content-table">
              <thead>
                <tr>
                  <th>Titel</th>
                  <th>Kategorie</th>
                  <th>Zuletzt bearbeitet von</th>
                  <th>Status</th>
                  <th>Zuletzt</th>
                  <th>Aktion</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6}>
                      <div className="ac-content-empty">Lade Tipps...</div>
                    </td>
                  </tr>
                ) : filtered.length > 0 ? (
                  filtered.map((t) => (
                    <tr key={t.id}>
                      <td>
                        <div className="font-extrabold text-ac-text">{t.title}</div>
                        <div className="text-xs text-ac-text/80">{t.excerpt}</div>
                      </td>
                      <td>{t.category}</td>
                      <td>{t.author}</td>
                      <td>
                        <StatusPill status={t.status} />
                      </td>
                      <td>{t.updatedAtLabel}</td>
                      <td>
                        <button
                          onClick={() => navigate(`/content/tips/${t.id}`)}
                          className="ac-ghostbtn ac-content-tablebtn"
                        >
                          Öffnen
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6}>
                      <div className="ac-content-empty">
                        Kein Treffer. Versuche andere Filter.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}